import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import {
  HIGHLIGHT_CLASSNAME,
  HIGHLIGHT_CONTAINER_PASSAGE,
  HIGHLIGHT_CONTAINER_QUESTION,
} from '@app/constants'
import { useScopedHighlighter } from '@app/helpers'
import { clearMarksErasable, setMarksErasable } from '@app/helpers/highlighter'

interface UseHighlightModeProps {
  itemId: string
  passageId: string
  // a11y: false in review mode (highlighting is disabled there); gates the global Ctrl+Shift+H listener
  enabled?: boolean
}

export const useHighlightMode = ({ itemId, passageId, enabled = true }: UseHighlightModeProps) => {
  const [highlightMode, setHighlightMode] = useState(false)
  const [eraseMode, setEraseMode] = useState(false)

  // a11y: entering review mode disables highlighting — clear any active mode so its listeners detach.
  // The toolbar that sets these modes no longer renders in review, so they can't be turned off via UI.
  useEffect(() => {
    if (!enabled) {
      setHighlightMode(false)
      setEraseMode(false)
    }
  }, [enabled])

  // Highlighter for questions and passages
  const questionHighlighter = useScopedHighlighter('q', itemId)
  const passageHighlighter = useScopedHighlighter('p', passageId)

  const questionContainer = document.getElementById(HIGHLIGHT_CONTAINER_QUESTION)
  const passageContainer = document.getElementById(HIGHLIGHT_CONTAINER_PASSAGE)

  const containers = useMemo(() => {
    return [questionContainer, passageContainer].filter(Boolean)
  }, [questionContainer, passageContainer])

  // a11y: re-arm every highlight as a focusable erase button. A selection-based add/erase rewrites the
  // marks (strips their attrs, re-adds only the sr markers), so surviving/new highlights lose the erase
  // affordance; call this after such an action while erase mode is on to keep them Tab-erasable.
  const decorateErasableMarks = useCallback(() => {
    containers.forEach(container => container && setMarksErasable(container, HIGHLIGHT_CLASSNAME))
  }, [containers])

  // Define highlighter by selection
  const getHighlighterBySelection = useCallback(() => {
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed) return null

    if (questionContainer?.contains(selection.anchorNode)) {
      return questionHighlighter
    }

    if (passageContainer?.contains(selection.anchorNode)) {
      return passageHighlighter
    }

    return null
  }, [passageHighlighter, questionHighlighter, questionContainer, passageContainer])

  // Get highlighter by clicked element
  const getHighlighterByElement = useCallback(
    (element: HTMLElement) => {
      if (questionContainer?.contains(element)) {
        return questionHighlighter
      }

      if (passageContainer?.contains(element)) {
        return passageHighlighter
      }

      return null
    },
    [passageHighlighter, questionHighlighter, questionContainer, passageContainer],
  )

  // Highlight the current selection (mouse mouseup, toolbar button, or keyboard shortcut)
  const handleMouseUp = useCallback(() => {
    const highlighter = getHighlighterBySelection()
    if (highlighter) {
      highlighter.onHighlightSelection()
    }
  }, [getHighlighterBySelection])

  // a11y: true when a non-collapsed selection sits inside a highlight container
  const hasValidSelection = useCallback(() => !!getHighlighterBySelection(), [getHighlighterBySelection])

  // a11y: highlight the current selection, or remove it when it is already highlighted
  const toggleHighlightOnSelection = useCallback(() => {
    const highlighter = getHighlighterBySelection()
    if (!highlighter) return

    if (highlighter.isSelectionHighlighted()) {
      highlighter.onRemoveHighlightsFromSelection()
    } else {
      highlighter.onHighlightSelection()
    }

    // a11y: the selection path rewrote the marks without erase affordances — re-arm them so surviving
    // (or newly added) highlights stay keyboard-erasable while erase mode is on.
    if (eraseMode) decorateErasableMarks()
  }, [getHighlighterBySelection, eraseMode, decorateErasableMarks])

  // a11y: remove the highlight(s) intersecting the current selection (toolbar Erase, keyboard)
  const unhighlightCurrentSelection = useCallback(() => {
    const highlighter = getHighlighterBySelection()
    if (highlighter) {
      highlighter.onRemoveHighlightsFromSelection()
      // a11y: re-arm surviving highlights as erase buttons (the selection erase stripped their affordances)
      if (eraseMode) decorateErasableMarks()
    }
  }, [getHighlighterBySelection, eraseMode, decorateErasableMarks])

  // Click handler for removing highlight in erase mode
  const handleClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const mark = target.closest(`span.${HIGHLIGHT_CLASSNAME}`)

      if (mark) {
        const highlighter = getHighlighterByElement(mark as HTMLElement)

        if (highlighter) {
          // Remove highlight (pass the mark so removal never depends on the sr-marker-adjusted selection)
          highlighter.onRemoveHighlight(mark)
        }
      }
    },
    [getHighlighterByElement],
  )

  // Automatic highlight when mouseup in highlight mode
  useEffect(() => {
    if (!highlightMode) return

    document.addEventListener('mouseup', handleMouseUp)
    return () => document.removeEventListener('mouseup', handleMouseUp)
  }, [highlightMode, getHighlighterBySelection, handleMouseUp])

  // a11y: keyboard path — Ctrl+Shift+H highlights/erases the current selection in place, so a
  // keyboard or screen-reader user never has to travel to the floating toolbar (Ready#7707).
  // Active whenever the highlight tool is mounted; no-ops (and lets the key through) unless a
  // valid selection exists. Assumes a single mounted highlight tool (mobile XOR desktop today).
  useEffect(() => {
    // a11y: disabled in review mode (highlighting is off there) — the toolbar already doesn't render,
    // this closes the keyboard path that otherwise stayed active.
    if (!enabled) return

    const handleKeyDown = (event: KeyboardEvent) => {
      const isHighlightShortcut =
        event.ctrlKey && event.shiftKey && !event.altKey && !event.metaKey && event.key.toLowerCase() === 'h'

      if (!isHighlightShortcut || !getHighlighterBySelection()) return

      event.preventDefault()
      toggleHighlightOnSelection()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [enabled, getHighlighterBySelection, toggleHighlightOnSelection])

  // a11y: with highlight mode on, Enter highlights the current selection (keyboard parity with the
  // mouse drag); scoped so it never hijacks Enter on a focused control.
  useEffect(() => {
    if (!highlightMode) return

    const handleEnterHighlight = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || !hasValidSelection()) return

      const target = event.target as HTMLElement | null
      if (target?.closest("button, a, input, textarea, select, [role='button']")) return

      event.preventDefault()
      handleMouseUp()
    }

    document.addEventListener('keydown', handleEnterHighlight)
    return () => document.removeEventListener('keydown', handleEnterHighlight)
  }, [highlightMode, hasValidSelection, handleMouseUp])

  // Remove highlight on click in erase mode
  useEffect(() => {
    if (!eraseMode) return

    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [eraseMode, getHighlighterByElement, handleClick])

  // a11y: keyboard erase — in erase mode every highlight becomes a focusable button; Enter (keydown)
  // or Space (keyup) removes the whole highlight, mirroring the mouse click-to-erase (Ready#7707).
  // a11y: refs so the erase effect below re-runs only on eraseMode changes, not on every render.
  // getHighlighterByElement is a fresh function each render; if the effect depended on it, its
  // cleanup (clearMarksErasable) would loop and strip tabindex off the mark you're focused on.
  const containersRef = useRef(containers)
  containersRef.current = containers
  const getHighlighterByElementRef = useRef(getHighlighterByElement)
  getHighlighterByElementRef.current = getHighlighterByElement

  useEffect(() => {
    if (!eraseMode) return

    const decorate = () =>
      containersRef.current.forEach(container => container && setMarksErasable(container, HIGHLIGHT_CLASSNAME))

    const getErasableMarks = () =>
      containersRef.current.flatMap(container =>
        container
          ? Array.from(
              container.querySelectorAll<HTMLElement>(`span.${HIGHLIGHT_CLASSNAME}[role="button"]`),
            )
          : [],
      )

    const eraseMark = (mark: HTMLElement) => {
      const highlighter = getHighlighterByElementRef.current(mark)
      if (!highlighter) return

      const removedIndex = getErasableMarks().indexOf(mark)
      // a11y: capture the mark's container now, before removal unwraps it — the focus fallback below.
      const container =
        containersRef.current.find(candidate => candidate?.contains(mark)) ?? null

      highlighter.onRemoveHighlight(mark)

      // onRemoveHighlight re-creates only the sr markers, so re-apply the erase affordances and move
      // focus to the next remaining highlight to keep the user in place.
      decorate()
      const remaining = getErasableMarks()
      // a11y: a multi-fragment highlight shrinks the list by more than one, so removedIndex can
      // overshoot; clamp to a valid index and land on the nearest remaining highlight, not <body>.
      if (remaining.length > 0) {
        remaining[Math.min(Math.max(removedIndex, 0), remaining.length - 1)].focus()
      } else if (container) {
        // a11y: erased the last highlight — move focus to the content region so it never falls to <body>.
        // Drop the temporary tabindex once focus leaves so it isn't left behind in the DOM.
        container.setAttribute('tabindex', '-1')
        container.addEventListener('blur', () => container.removeAttribute('tabindex'), { once: true })
        container.focus({ preventScroll: true })
      }
    }

    const getFocusedMark = (target: EventTarget | null) =>
      (target as HTMLElement)?.closest?.(`span.${HIGHLIGHT_CLASSNAME}[role="button"]`) as
        | HTMLElement
        | null

    const handleKeyDown = (event: KeyboardEvent) => {
      const mark = getFocusedMark(event.target)
      if (!mark) return

      // a11y: Enter or Ctrl+Shift+H erases the focused highlight; Space erases on keyup (button
      // convention) and is prevented here to suppress page scroll.
      const isEraseShortcut =
        event.ctrlKey && event.shiftKey && !event.altKey && !event.metaKey && event.key.toLowerCase() === 'h'

      if (event.key === 'Enter' || isEraseShortcut) {
        event.preventDefault()
        eraseMark(mark)
      } else if (event.key === ' ') {
        event.preventDefault()
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      const mark = getFocusedMark(event.target)
      if (mark && event.key === ' ') {
        event.preventDefault()
        eraseMark(mark)
      }
    }

    decorate()
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
      containersRef.current.forEach(container => container && clearMarksErasable(container, HIGHLIGHT_CLASSNAME))
    }
  }, [eraseMode])

  // Manage cursor through CSS class
  useEffect(() => {
    if (highlightMode) {
      containers.forEach(container => {
        container?.classList.add('highlight-mode')
      })
    } else if (eraseMode) {
      containers.forEach(container => {
        container?.classList.add('erase-mode')
      })
    } else {
      containers.forEach(container => {
        container?.classList.remove('highlight-mode', 'erase-mode')
      })
    }

    return () => {
      containers.forEach(container => {
        container?.classList.remove('highlight-mode', 'erase-mode')
      })
    }
  }, [highlightMode, eraseMode, containers])

  const reset = useCallback(() => {
    setHighlightMode(false)
    setEraseMode(false)
  }, [])

  // Toggle highlight mode
  const toggleHighlightMode = useCallback(() => {
    setEraseMode(false) // Off erase mode
    setHighlightMode(prev => !prev)
  }, [])

  // Toggle erase mode
  const toggleEraseMode = useCallback(() => {
    setHighlightMode(false) // Off highlight mode
    setEraseMode(prev => !prev)
  }, [])

  return {
    highlightMode,
    eraseMode,
    toggleHighlightMode,
    toggleEraseMode,
    reset,
    highlightCurrentSelection: handleMouseUp,
    unhighlightCurrentSelection,
    hasValidSelection,
  }
}
